import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { Users, Copy, Share2, Gem, TrendingUp } from 'lucide-react';
import { api, compact, timeAgo } from '../lib/api';
import { Avatar, SkeletonList, Empty, SectionHead } from '../components/ui';

export default function Referrals() {
  const { data, isLoading } = useQuery({ queryKey: ['referrals'], queryFn: () => api.get('/referrals/me').then(r => r.data) });

  if (isLoading || !data) return <SkeletonList n={3} h="h-40" />;

  const share = async () => {
    const text = `Join me on Wall of Freedom — shop local, level up your hero, fight friends! 🔥`;
    if (navigator.share) { try { await navigator.share({ title: 'Wall of Freedom', text, url: data.link }); return; } catch {} }
    await navigator.clipboard.writeText(data.link);
    toast.success('Link copied!');
  };

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card p-6 bg-gradient-to-br from-grape-500 to-brand-600 text-white text-center">
        <Users size={26} className="mx-auto mb-2 opacity-80" />
        <h1 className="font-display text-2xl font-extrabold">Invite & earn forever</h1>
        <p className="text-white/85 text-sm mt-1">
          They get <b>{data.rules.signup_referee}</b> points to start.
          You get <b>{data.rules.signup_referrer}</b> + <b>{data.rules.purchase_referrer_pct}%</b> of everything they earn shopping — for life.
        </p>

        <div className="bg-white rounded-2xl p-4 mt-5 inline-block">
          <QRCodeSVG value={data.link} size={150} level="M" fgColor="#0f172a" />
        </div>

        <p className="font-black text-3xl tracking-[0.25em] mt-4">{data.code}</p>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <button onClick={() => { navigator.clipboard.writeText(data.link); toast.success('Copied!'); }}
            className="btn bg-white/20 text-white btn-sm"><Copy size={14} /> Copy link</button>
          <button onClick={share} className="btn bg-white text-grape-700 btn-sm font-extrabold">
            <Share2 size={14} /> Share</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="card p-4 text-center">
          <Users size={18} className="mx-auto text-grape-500 mb-1" />
          <p className="font-black text-2xl">{data.stats.invited}</p>
          <p className="text-[10px] font-bold text-ink-400 uppercase">Friends invited</p>
        </div>
        <div className="card p-4 text-center">
          <Gem size={18} className="mx-auto text-brand-500 mb-1" />
          <p className="font-black text-2xl">{compact(data.stats.earned)}</p>
          <p className="text-[10px] font-bold text-ink-400 uppercase">Points earned</p>
        </div>
      </div>

      <div className="card p-4 bg-brand-50 border-brand-200">
        <p className="font-bold text-sm text-brand-800 flex items-center gap-1.5">
          <TrendingUp size={15} /> How it works
        </p>
        <ul className="text-xs text-brand-700 font-semibold mt-2 space-y-1">
          <li>1. Friend signs up → you +{data.rules.signup_referrer}💎, they +{data.rules.signup_referee}💎</li>
          <li>2. They buy 100 RON at a shop → they get 100💎, you get {data.rules.purchase_referrer_pct}💎</li>
          <li>3. They also get +{data.rules.purchase_referee_pct}% bonus on every purchase</li>
          <li>4. Repeat forever — no cap 🚀</li>
        </ul>
      </div>

      <SectionHead title="Your network" />
      {!data.list.length ? <Empty icon={Users} title="No invites yet" sub="Share your link and start earning passively." />
        : <div className="card p-2 space-y-1">
          {data.list.map(r => (
            <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
              <Avatar src={r.avatar_url} name={r.username} size={38} />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{r.display_name || r.username}</p>
                <p className="text-[11px] text-ink-400 font-semibold">
                  Joined {timeAgo(r.joined_at)} · {r.purchases} purchases {r.level && `· L${r.level}`}
                </p>
              </div>
              <span className="chip-mint">+{compact(r.points_referrer)}💎</span>
            </div>))}
        </div>}
    </div>
  );
}